import { useEffect, useRef, useState, type ReactNode } from "react";
import styled from "styled-components";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { Button } from "./Button";

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + ${spacing.x05}px);
  right: 0;
  z-index: 10;
  min-width: 220px;
  box-sizing: border-box;
  overflow-y: auto;
  background-color: ${colors.backgroundLight};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: ${spacing.x2}px;
`;

interface DropdownProps {
  label: ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  small?: boolean;
  panelWidth?: number;
}

const VIEWPORT_MARGIN = spacing.x2;
const MIN_PANEL_HEIGHT = 240;

export const Dropdown = ({
  label,
  children,
  small,
  panelWidth,
}: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const [panelSize, setPanelSize] = useState<{
    maxHeight: number;
    width?: number;
  }>({ maxHeight: MIN_PANEL_HEIGHT });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !target.closest?.('[role="listbox"]')
      ) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <Container ref={containerRef}>
      <Button
        type="button"
        $color="secondary"
        $variant="outlined"
        $small={small}
        onClick={() => {
          const rect = containerRef.current?.getBoundingClientRect();
          if (!open && rect) {
            setPanelSize({
              maxHeight: Math.max(
                MIN_PANEL_HEIGHT,
                window.innerHeight - rect.bottom - spacing.x05 - VIEWPORT_MARGIN,
              ),
              width: panelWidth
                ? Math.min(panelWidth, rect.right - VIEWPORT_MARGIN)
                : undefined,
            });
          }
          setOpen(!open);
        }}
      >
        {label}
      </Button>
      {open && (
        <Panel style={panelSize}>
          {typeof children === "function"
            ? children(() => setOpen(false))
            : children}
        </Panel>
      )}
    </Container>
  );
};
